import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const LOGO = "/leadmato-logo.png";

const NAV_LINKS = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/roadmap", label: "Roadmap" },
  { to: "/blog", label: "Blog" },
  { to: "/developers", label: "API Docs" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `hover:text-white transition-colors duration-200 ${isActive ? "text-white font-medium" : "text-white/70"}`;

  return (
    <header
      data-testid="marketing-navbar"
      className={`sticky top-0 z-40 bg-[color:var(--lm-dark)] transition-shadow duration-300 ${
        scrolled
          ? "shadow-[0_4px_20px_-8px_rgba(0,0,0,0.35)] border-b border-[color:var(--lm-dark-border)]"
          : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="Leadmato home" data-testid="nav-logo">
          <img
            src={LOGO}
            alt="Leadmato"
            className="h-8 md:h-9 w-auto max-w-[170px] object-contain select-none"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-sm">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={linkClass}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-signin"
            className="hidden md:inline text-sm text-white/70 hover:text-white transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            data-testid="nav-cta-start"
            className="inline-flex items-center gap-1.5 bg-[color:var(--lm-brand-green)] text-[color:var(--lm-dark)] text-sm font-semibold px-4 py-2 rounded-lg hover:bg-white hover:text-[color:var(--lm-dark)] transition-colors"
          >
            Get started
            <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </Link>
          <button
            className="lg:hidden text-white p-2 -mr-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[color:var(--lm-dark)] border-t border-[color:var(--lm-dark-border)] px-5 py-4">
          <nav className="flex flex-col gap-2">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="text-white/80 hover:text-white text-sm py-2 border-b border-[color:var(--lm-dark-border)] last:border-0"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="text-white/70 hover:text-white text-sm py-2"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 bg-[color:var(--lm-brand-green)] text-[color:var(--lm-dark)] text-sm font-semibold px-4 py-2.5 rounded-lg mt-2"
              data-testid="nav-mobile-cta"
            >
              Get started
              <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
